import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Trash2, RotateCcw, Copy, Check, Eye, EyeOff, Shield, ShieldOff, Key, ChevronDown, Pencil } from 'lucide-react'
import api from '../../services/api'

const fmtDate = (s) => {
  if (!s) return 'Nunca'
  try {
    const d = new Date(s)
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  } catch { return s }
}

const PERMISSAO_LABELS = {
  leitura: 'Somente Leitura',
  leitura_vendas: 'Leitura + Vendas',
}

const PERMISSAO_CORES = {
  leitura: 'bg-blue-100 text-blue-800',
  leitura_vendas: 'bg-purple-100 text-purple-800',
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 transition-colors"
      title="Copiar token"
    >
      {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
      {copied ? 'Copiado!' : 'Copiar'}
    </button>
  )
}

function ModalNovoToken({ organizadores, onClose, onCriado }) {
  const [form, setForm] = useState({ nome_cliente: '', organizador_ids: [], permissao: 'leitura' })
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [orgOpen, setOrgOpen] = useState(false)
  const orgRef = useRef(null)

  const orgsLista = Array.isArray(organizadores) ? organizadores : []
  const orgsFiltrados = orgsLista.filter(o => o.name?.toLowerCase().includes(orgSearch.toLowerCase()))
  const orgsSelecionados = orgsLista.filter(o => form.organizador_ids.includes(o.id))

  const toggleOrg = (id) => {
    setForm(f => ({
      ...f,
      organizador_ids: f.organizador_ids.includes(id)
        ? f.organizador_ids.filter(x => x !== id)
        : [...f.organizador_ids, id]
    }))
  }

  useEffect(() => {
    function handleClick(e) {
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.nome_cliente.trim()) { setErro('Informe o nome do cliente.'); return }
    if (form.organizador_ids.length === 0) { setErro('Selecione ao menos um organizador.'); return }
    setLoading(true)
    setErro('')
    try {
      const res = await api.post('/auth/super-admin/tokens-externos', {
        nome_cliente: form.nome_cliente.trim(),
        organizador_ids: form.organizador_ids,
        permissao: form.permissao,
      })
      onCriado(res.data)
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao criar token.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Key size={18} /> Novo Token de Acesso
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do cliente (identificação)
            </label>
            <input
              type="text"
              value={form.nome_cliente}
              onChange={e => setForm(f => ({ ...f, nome_cliente: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Canal AgroVertentes"
              maxLength={200}
              required
            />
          </div>

          <div ref={orgRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organizadores <span className="text-gray-400 font-normal">(pode selecionar vários)</span>
            </label>

            {/* Tags dos selecionados */}
            {orgsSelecionados.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {orgsSelecionados.map(o => (
                  <span key={o.id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                    {o.name}
                    <button type="button" onClick={() => toggleOrg(o.id)} className="hover:text-blue-900">×</button>
                  </span>
                ))}
              </div>
            )}

            <div
              className="w-full border rounded-lg px-3 py-2 text-sm cursor-pointer flex items-center justify-between bg-white"
              onClick={() => setOrgOpen(o => !o)}
            >
              <span className="text-gray-400">Buscar e selecionar...</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${orgOpen ? 'rotate-180' : ''}`} />
            </div>

            {orgOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <input
                    autoFocus
                    type="text"
                    value={orgSearch}
                    onChange={e => setOrgSearch(e.target.value)}
                    placeholder="Buscar organizador..."
                    className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <ul className="max-h-48 overflow-y-auto">
                  {orgsFiltrados.length === 0 && (
                    <li className="px-3 py-2 text-sm text-gray-400">Nenhum resultado</li>
                  )}
                  {orgsFiltrados.map(o => {
                    const selected = form.organizador_ids.includes(o.id)
                    return (
                      <li
                        key={o.id}
                        className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-blue-50 ${selected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        onClick={() => toggleOrg(o.id)}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                          {selected && <Check size={10} className="text-white" />}
                        </span>
                        {o.name}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Permissão</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="leitura"
                  checked={form.permissao === 'leitura'}
                  onChange={() => setForm(f => ({ ...f, permissao: 'leitura' }))}
                  className="mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium">Somente Leitura</span>
                  <p className="text-xs text-gray-500">Consulta de organizadores, leilões e lotes</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="leitura_vendas"
                  checked={form.permissao === 'leitura_vendas'}
                  onChange={() => setForm(f => ({ ...f, permissao: 'leitura_vendas' }))}
                  className="mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium">Leitura + Vendas</span>
                  <p className="text-xs text-gray-500">Inclui busca de compradores e registro de vendas</p>
                </div>
              </label>
            </div>
          </div>

          {erro && <p className="text-red-600 text-sm">{erro}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Gerando...' : 'Gerar Token'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ModalEditarToken({ token, organizadores, onClose, onSalvo }) {
  const [form, setForm] = useState({
    nome_cliente: token.nome_cliente,
    organizador_ids: (token.organizadores || []).map(o => o.id),
    permissao: token.permissao,
  })
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [orgSearch, setOrgSearch] = useState('')
  const [orgOpen, setOrgOpen] = useState(false)
  const orgRef = useRef(null)

  const orgsLista = Array.isArray(organizadores) ? organizadores : []
  const orgsFiltrados = orgsLista.filter(o => o.name?.toLowerCase().includes(orgSearch.toLowerCase()))
  const orgsSelecionados = orgsLista.filter(o => form.organizador_ids.includes(o.id))

  const toggleOrg = (id) => {
    setForm(f => ({
      ...f,
      organizador_ids: f.organizador_ids.includes(id)
        ? f.organizador_ids.filter(x => x !== id)
        : [...f.organizador_ids, id]
    }))
  }

  useEffect(() => {
    function handleClick(e) {
      if (orgRef.current && !orgRef.current.contains(e.target)) setOrgOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.nome_cliente.trim()) { setErro('Informe o nome do cliente.'); return }
    if (form.organizador_ids.length === 0) { setErro('Selecione ao menos um organizador.'); return }
    setLoading(true)
    setErro('')
    try {
      const res = await api.patch(`/auth/super-admin/tokens-externos/${token.id}`, {
        nome_cliente: form.nome_cliente.trim(),
        organizador_ids: form.organizador_ids,
        permissao: form.permissao,
      })
      onSalvo(res.data)
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao salvar.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Pencil size={18} /> Editar Token
        </h2>
        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 mb-4">
          O token não é alterado — apenas nome, organizadores e permissão.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do cliente</label>
            <input
              type="text"
              value={form.nome_cliente}
              onChange={e => setForm(f => ({ ...f, nome_cliente: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={200}
              required
            />
          </div>

          <div ref={orgRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organizadores <span className="text-gray-400 font-normal">(pode selecionar vários)</span>
            </label>
            {orgsSelecionados.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {orgsSelecionados.map(o => (
                  <span key={o.id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                    {o.name}
                    <button type="button" onClick={() => toggleOrg(o.id)} className="hover:text-blue-900">×</button>
                  </span>
                ))}
              </div>
            )}
            <div
              className="w-full border rounded-lg px-3 py-2 text-sm cursor-pointer flex items-center justify-between bg-white"
              onClick={() => setOrgOpen(o => !o)}
            >
              <span className="text-gray-400">Buscar e selecionar...</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${orgOpen ? 'rotate-180' : ''}`} />
            </div>
            {orgOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <div className="p-2 border-b border-gray-100">
                  <input
                    autoFocus
                    type="text"
                    value={orgSearch}
                    onChange={e => setOrgSearch(e.target.value)}
                    placeholder="Buscar organizador..."
                    className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={e => e.stopPropagation()}
                  />
                </div>
                <ul className="max-h-48 overflow-y-auto">
                  {orgsFiltrados.length === 0 && (
                    <li className="px-3 py-2 text-sm text-gray-400">Nenhum resultado</li>
                  )}
                  {orgsFiltrados.map(o => {
                    const selected = form.organizador_ids.includes(o.id)
                    return (
                      <li
                        key={o.id}
                        className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 hover:bg-blue-50 ${selected ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        onClick={() => toggleOrg(o.id)}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                          {selected && <Check size={10} className="text-white" />}
                        </span>
                        {o.name}
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Permissão</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" value="leitura" checked={form.permissao === 'leitura'}
                  onChange={() => setForm(f => ({ ...f, permissao: 'leitura' }))} className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Somente Leitura</span>
                  <p className="text-xs text-gray-500">Consulta de organizadores, leilões e lotes</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" value="leitura_vendas" checked={form.permissao === 'leitura_vendas'}
                  onChange={() => setForm(f => ({ ...f, permissao: 'leitura_vendas' }))} className="mt-0.5" />
                <div>
                  <span className="text-sm font-medium">Leitura + Vendas</span>
                  <p className="text-xs text-gray-500">Inclui busca de compradores e registro de vendas</p>
                </div>
              </label>
            </div>
          </div>

          {erro && <p className="text-red-600 text-sm">{erro}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ModalTokenCriado({ token, onClose }) {
  const [visivel, setVisivel] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Check size={20} className="text-green-600" />
          <h2 className="text-lg font-semibold text-gray-800">Token gerado com sucesso</h2>
        </div>
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          <strong>Atenção:</strong> este token é exibido apenas UMA VEZ. Copie agora e armazene com segurança.
          Não será possível recuperá-lo depois.
        </p>

        <div className="mb-1 text-xs text-gray-500 font-medium">Token</div>
        <div className="flex items-center gap-2 mb-4">
          <code className="flex-1 bg-gray-100 rounded-lg px-3 py-2 text-sm font-mono break-all select-all">
            {visivel ? token.token : '•'.repeat(Math.min(token.token.length, 40))}
          </code>
          <button
            onClick={() => setVisivel(v => !v)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            title={visivel ? 'Ocultar' : 'Revelar'}
          >
            {visivel ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          <CopyButton text={token.token} />
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-5">
          <div><span className="font-medium">Cliente:</span> {token.nome_cliente}</div>
          <div className="col-span-2"><span className="font-medium">Organizadores:</span> {(token.organizadores || []).map(o => o.nome).join(', ')}</div>
          <div><span className="font-medium">Permissão:</span> {PERMISSAO_LABELS[token.permissao]}</div>
          <div><span className="font-medium">Prefixo:</span> {token.token_prefix}…</div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Confirmar — já copiei o token
          </button>
        </div>
      </div>
    </div>
  )
}

export default function TokensExternos() {
  const [tokens, setTokens] = useState([])
  const [organizadores, setOrganizadores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNovoModal, setShowNovoModal] = useState(false)
  const [tokenCriado, setTokenCriado] = useState(null)
  const [tokenEditando, setTokenEditando] = useState(null)

  const fetchTokens = useCallback(() => {
    setLoading(true)
    api.get('/auth/super-admin/tokens-externos')
      .then(r => setTokens(r.data))
      .catch(() => setTokens([]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    fetchTokens()
    api.get('/organizadores', { params: { per_page: 200 } })
      .then(r => setOrganizadores(r.data?.data ?? r.data?.items ?? []))
      .catch(() => {})
  }, [fetchTokens])

  const handleRevogar = async (id) => {
    if (!window.confirm('Revogar este token? O acesso será bloqueado imediatamente.')) return
    try {
      await api.delete(`/auth/super-admin/tokens-externos/${id}`)
      setTokens(ts => ts.map(t => t.id === id ? { ...t, ativo: false } : t))
    } catch {
      alert('Erro ao revogar token.')
    }
  }

  const handleSalvo = (tokenAtualizado) => {
    setTokens(ts => ts.map(t => t.id === tokenAtualizado.id ? tokenAtualizado : t))
    setTokenEditando(null)
  }

  const handleReativar = async (id) => {
    try {
      const res = await api.patch(`/auth/super-admin/tokens-externos/${id}/reativar`)
      setTokens(ts => ts.map(t => t.id === id ? res.data : t))
    } catch {
      alert('Erro ao reativar token.')
    }
  }

  const handleTokenCriado = (token) => {
    setShowNovoModal(false)
    setTokenCriado(token)
    setTokens(ts => [token, ...ts])
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Key size={24} /> Tokens de Acesso Externo
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Tokens Bearer para o Painel de Lances consumir a API do Gestão.
          </p>
        </div>
        <button
          onClick={() => setShowNovoModal(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          <Plus size={16} /> Novo Token
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Carregando...</div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Key size={40} className="mx-auto mb-3 opacity-40" />
          <p>Nenhum token criado ainda.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Cliente</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Organizadores</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Permissão</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Prefixo</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Último uso</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Criado em</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tokens.map(t => (
                <tr key={t.id} className={!t.ativo ? 'opacity-50 bg-gray-50' : ''}>
                  <td className="px-4 py-3 font-medium text-gray-900">{t.nome_cliente}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(t.organizadores || []).map(o => (
                        <span key={o.id} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">{o.nome}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PERMISSAO_CORES[t.permissao]}`}>
                      {PERMISSAO_LABELS[t.permissao]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{t.token_prefix}…</code>
                  </td>
                  <td className="px-4 py-3">
                    {t.ativo
                      ? <span className="inline-flex items-center gap-1 text-green-700 text-xs"><Shield size={12} /> Ativo</span>
                      : <span className="inline-flex items-center gap-1 text-gray-500 text-xs"><ShieldOff size={12} /> Revogado</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    <div>{fmtDate(t.last_used_at)}</div>
                    {t.last_used_ip && <div className="text-gray-400">{t.last_used_ip}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setTokenEditando(t)}
                        className="p-1.5 rounded hover:bg-blue-50 text-blue-500"
                        title="Editar token"
                      >
                        <Pencil size={14} />
                      </button>
                      {t.ativo ? (
                        <button
                          onClick={() => handleRevogar(t.id)}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500"
                          title="Revogar token"
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReativar(t.id)}
                          className="p-1.5 rounded hover:bg-green-50 text-green-600"
                          title="Reativar token"
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNovoModal && (
        <ModalNovoToken
          organizadores={organizadores}
          onClose={() => setShowNovoModal(false)}
          onCriado={handleTokenCriado}
        />
      )}

      {tokenCriado && (
        <ModalTokenCriado
          token={tokenCriado}
          onClose={() => setTokenCriado(null)}
        />
      )}

      {tokenEditando && (
        <ModalEditarToken
          token={tokenEditando}
          organizadores={organizadores}
          onClose={() => setTokenEditando(null)}
          onSalvo={handleSalvo}
        />
      )}
    </div>
  )
}
